import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface RenameColumnDialogProps {
  columnName: string;
  onConfirm: (newLabel: string) => void;
  onCancel: () => void;
}

export function RenameColumnDialog({ columnName, onConfirm, onCancel }: RenameColumnDialogProps) {
  const { t } = useLanguage();
  const [newLabel, setNewLabel] = useState(columnName);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newLabel.trim()) {
      onConfirm(newLabel.trim());
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onCancel}
    >
      <div
        className="rounded-lg shadow-xl p-6 w-full max-w-md"
        style={{
          backgroundColor: 'var(--md-surface)',
          color: 'var(--md-on-surface)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{t('dialogs.renameColumn.title')}</h2>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:bg-opacity-10"
            style={{ backgroundColor: 'var(--md-surface-variant)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-4">
          <form onSubmit={handleSubmit}>
            <label className="block mb-2 text-sm font-medium">
              {t('dialogs.renameColumn.label')}
            </label>
            <textarea
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={t('dialogs.renameColumn.placeholder')}
              rows={3}
              className="w-full px-3 py-2 rounded border resize-none"
              style={{
                backgroundColor: 'var(--md-surface-container)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
              autoFocus
              onFocus={(e) => e.target.select()}
            />
            <p className="mt-2 text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
              {t('dialogs.renameColumn.hint')}
              <br />
              <strong>{t('dialogs.renameColumn.tip')}</strong>
            </p>
          </form>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded"
            style={{
              backgroundColor: 'var(--md-surface-variant)',
              color: 'var(--md-on-surface-variant)',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!newLabel.trim()}
            className="px-4 py-2 rounded font-medium"
            style={{
              backgroundColor: newLabel.trim() ? 'var(--md-primary)' : 'var(--md-surface-variant)',
              color: newLabel.trim() ? 'var(--md-on-primary)' : 'var(--md-on-surface-variant)',
              cursor: newLabel.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            {t('dialogs.renameColumn.changeBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
